import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useOpenIndividual from '../../../hooks/useOpenIndividual';
import { INDIVIDUAL_ENTRY_POINTS } from '../../../constants/individualOpenModes';
import {
  FaArrowLeft,
  FaRegStar,
  FaRegCommentDots,
  FaSortAmountDown,
  FaSortAmountUp,
} from 'react-icons/fa';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, MainSection, BackButton } from '../styles';
import {
  FiltersBar,
  HeaderStats,
  HeaderStat,
  EmployeeRow,
  EmployeeRowMain,
  EmployeeInfo,
  BenchMeta,
  DaysCell,
  LoadMoreButton,
  SortButton,
  TechBadge,
  TechBadgeMore,
  TechBadgeLess,
} from './styles';
import { PlacementActivitySummary } from './PlacementActivitySummary';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ResultsCount from '../../common/ResultsCount';
import EmployeeSearch from '../../common/EmployeeSearch';
import { EmployeeSearchProvider } from '../../common/EmployeeSearch/contexts/EmployeeSearchProvider';
import ClusterSelector from '../../common/ClusterSelector';
import BusinessUnitSelector from '../../common/BusinessUnitSelector';
import PositionSelector from '../../common/PositionSelector';
import TechnologyFilter from '../../common/TechnologyFilter';
import { benchApi } from '../../../services/api/benchApi';
import { useAbility } from '../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';
import { BENCH_TYPES, getBenchTypeLabel, getBenchTypeColor } from '../../../constants/benchTypes';
import { EMPLOYEE_STATUSES } from '../../../constants/employeeStatuses';
import { getSalaryPeriodLabel, getSalaryAmountTypeLabel } from '../../../constants/benchTypes';
import { formatAmount } from '../../../utils/formatUtils';
import { useBenchBusinessUnitFacets } from '../../../hooks/useBenchBusinessUnitFacets';
import { getBusinessUnitFacetCount } from '../../../utils/benchBusinessUnitFacets';
import { useBenchPositionFacets } from '../../../hooks/useBenchPositionFacets';
import { getPositionFacetCount, getPositionFacetFooterNote } from '../../../utils/benchPositionFacets';
import {
  BENCH_REPORT_KEYS,
  BENCH_REPORT_BU_SCOPE,
  BENCH_BUSINESS_UNIT_COUNT_TOOLTIP,
  BENCH_BUSINESS_UNIT_EMPTY_LABEL,
  BENCH_POSITION_COUNT_TOOLTIP,
  BENCH_POSITION_EMPTY_LABEL,
  BENCH_POSITION_UNTYPED_GROUP_LABEL,
} from '../../../constants/benchReportFacets';
import { formatDateOnly } from '../../../utils/dateOnly';

const decodeHtmlEntities = (str) => {
  if (!str) { return ''; }
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
};

const CommentPopup = ({ html }) => {
  const [visible, setVisible] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.innerHTML = decodeHtmlEntities(html);
    }
  }, [html]);

  return (
    <span
      style={{ position: 'relative', display: 'flex' }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <span style={{ color: '#94A3B8', fontSize: '14px', display: 'flex', cursor: 'default' }}>
        <FaRegCommentDots />
      </span>
      <div
        ref={contentRef}
        style={{
          display: visible ? 'block' : 'none',
          position: 'absolute',
          bottom: 'calc(100% + 8px)',
          right: 0,
          zIndex: 9999,
          backgroundColor: 'white',
          padding: '12px',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          minWidth: '200px',
          maxWidth: '360px',
          width: 'max-content',
          color: '#475569',
          fontSize: '13px',
          lineHeight: 1.6,
          pointerEvents: 'none',
        }}
      />
    </span>
  );
};

// ADR-197: date-only values — the stored calendar day, never a local instant
const formatDate = (dateStr) => formatDateOnly(dateStr);

const StatusChip = ({ label, color, backgroundColor, borderColor }) => (
  <span style={{
    fontSize: '10px',
    fontWeight: 600,
    color,
    backgroundColor,
    border: `1px solid ${borderColor}`,
    borderRadius: '4px',
    padding: '1px 5px',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  }}>
    {label}
  </span>
);

const BenchDatesCell = ({ status, benchDays, daysUntilStart, benchStartDate, benchEndDate, employeeResignDate, children }) => {
  const isJoining = status === EMPLOYEE_STATUSES.JOINING;
  const endDate = benchEndDate || employeeResignDate;
  const statusChip = benchEndDate
    ? <StatusChip label="new project" color="#1D4ED8" backgroundColor="#EFF6FF" borderColor="#BFDBFE" />
    : employeeResignDate
      ? <StatusChip label="resigned" color="#DC2626" backgroundColor="#FEF2F2" borderColor="#FECACA" />
      : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
      {isJoining ? (
        <DaysCell $days={-1}>in {daysUntilStart}d</DaysCell>
      ) : (
        <DaysCell $days={benchDays}>for {benchDays}d</DaysCell>
      )}
      {(benchStartDate || endDate) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {benchStartDate && <DateChip prefix="from" date={benchStartDate} />}
          {!isJoining && endDate && <DateChip prefix="until" date={endDate} />}
          {!isJoining && statusChip}
        </div>
      )}
      {children && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {children}
        </div>
      )}
    </div>
  );
};

const DateChip = ({ prefix, date }) => (
  <span style={{
    fontSize: '11px',
    fontWeight: 500,
    color: '#64748B',
    backgroundColor: '#F1F5F9',
    border: '1px solid #E2E8F0',
    borderRadius: '4px',
    padding: '1px 6px',
    whiteSpace: 'nowrap',
  }}>
    {prefix} {formatDate(date)}
  </span>
);

const BenchReport = () => {
  const navigate = useNavigate();
  const openIndividual = useOpenIndividual();
  const location = useLocation();
  const ability = useAbility();
  const canReadSalary = ability.can(ACTIONS.READ, SUBJECTS.BENCH_SALARY);
  const mainSectionRef = useRef(null);
  const scrollRestoredRef = useRef(false);

  // Restore state when returning from employee profile
  const restored = location.state?.benchState;

  // Filter state — initialized from restored state if available
  const [clusterId, setClusterId] = useState(restored?.clusterId || '');
  const [businessUnitId, setBusinessUnitId] = useState(restored?.businessUnitId || '');
  const [benchType, setBenchType] = useState(restored?.benchType || '');
  const [flagAsStar, setFlagAsStar] = useState(restored?.flagAsStar || '');
  const [selectedEmployee, setSelectedEmployee] = useState(restored?.selectedEmployee || null);
  const [orderDirection, setOrderDirection] = useState(restored?.orderDirection || 'desc');
  const [selectedTechs, setSelectedTechs] = useState(restored?.selectedTechs || []); // [{ id, name }]
  const [selectedPositionIds, setSelectedPositionIds] = useState(restored?.selectedPositionIds || []); // ADR-110

  // ADR-108: Business Unit options = BUs with individuals on bench in the selected
  // Cluster (other filters do not narrow them). Out-of-scope selection is cleared.
  const {
    businessUnitsData,
    countMode: businessUnitCountMode,
    isLoading: isLoadingBusinessUnits,
    failed: businessUnitFacetsFailed,
  } = useBenchBusinessUnitFacets({
    report: BENCH_REPORT_KEYS.BENCH,
    clusterId,
    businessUnitId,
    onSelectionOutOfScope: () => setBusinessUnitId(''),
  });

  // ADR-110: Position options = positions of individuals on bench in the selected Delivery
  // Center and Business Unit, grouped by org unit type. Selections with 0 are kept.
  const {
    positionsData,
    facet: positionFacet,
    isLoading: isLoadingPositions,
    failed: positionFacetsFailed,
  } = useBenchPositionFacets({
    clusterId,
    businessUnitId,
    selectedPositionIds,
    onSelectionChange: setSelectedPositionIds,
  });

  // Data state
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [expandedTech, setExpandedTech] = useState({});

  const fetchReport = useCallback(async (cursor = undefined, append = false) => {
    try {
      if (!append) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const params = {
        limit: 50,
        orderDirection,
        cursor,
      };

      if (clusterId) {
        params.clusterId = clusterId;
      }
      if (businessUnitId) {
        params.businessUnitId = businessUnitId;
        params.buScope = BENCH_REPORT_BU_SCOPE;
      }
      if (benchType) {
        params.benchType = benchType;
      }
      if (flagAsStar !== '') {
        params.flagAsStar = flagAsStar === 'true';
      }
      if (selectedEmployee) {
        params.employeeId = selectedEmployee.id;
      }
      if (selectedTechs.length > 0) {
        params.leafSegmentIds = selectedTechs.map((t) => t.id);
      }
      if (selectedPositionIds.length > 0) {
        params.positionIds = selectedPositionIds;
      }

      const data = await benchApi.getBenchReport(params);

      if (append) {
        setEmployees((prev) => [...prev, ...data.employees]);
      } else {
        setEmployees(data.employees);
      }

      setReport(data);
    } catch (error) {
      console.error('Error fetching bench report:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [clusterId, businessUnitId, benchType, flagAsStar, selectedEmployee, orderDirection, selectedTechs, selectedPositionIds]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleLoadMore = () => {
    if (report?.pageInfo?.hasMore && report.pageInfo.cursor) {
      fetchReport(report.pageInfo.cursor, true);
    }
  };

  const toggleSortDirection = () => {
    setOrderDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  };

  // Restore scroll position after data loads on return from employee profile
  useEffect(() => {
    if (!isLoading && restored?.scrollTop && !scrollRestoredRef.current && mainSectionRef.current) {
      mainSectionRef.current.scrollTop = restored.scrollTop;
      scrollRestoredRef.current = true;
    }
  }, [isLoading, restored]);

  // Filter state carried to the Individual page and restored on return. One builder for
  // every navigation so a new filter cannot be forgotten in one of them (ADR-110 §6).
  const buildBenchState = useCallback(() => ({
    clusterId,
    businessUnitId,
    benchType,
    flagAsStar,
    selectedEmployee,
    orderDirection,
    selectedTechs,
    selectedPositionIds,
    scrollTop: mainSectionRef.current?.scrollTop || 0,
  }), [clusterId, businessUnitId, benchType, flagAsStar, selectedEmployee, orderDirection, selectedTechs, selectedPositionIds]);

  const handleNavigateToEmployee = useCallback((emp) => {
    const benchState = buildBenchState();
    // ADR-109 — auto: Edit when the user manages the individual (Individuals-list rule)
    openIndividual(emp.employee.id, {
      entryPoint: INDIVIDUAL_ENTRY_POINTS.BENCH_REPORT,
      returnTo: '/reports/bench',
      returnLabel: 'Bench Report',
      state: { benchState },
    });
  }, [buildBenchState, openIndividual]);

  // Deep-link to the Placement Activity tab (ADR-008).
  const handleOpenPlacementActivity = useCallback((emp) => {
    const benchState = buildBenchState();
    // ADR-009: the Placement Activity tab is no longer forced read-only via URL.
    // Authorization is driven by the API's per-activity `permissions` projection —
    // home-BU managers see full control, cross-BU directors with PlacementActivityBench
    // can edit/create while the consultant is in the bench window, others see "Read only".
    // ADR-109: opens through the same auto resolution as the row click.
    openIndividual(emp.employee.id, {
      entryPoint: INDIVIDUAL_ENTRY_POINTS.BENCH_REPORT_PLACEMENT_ACTIVITY,
      tab: 'PLACEMENT_ACTIVITY',
      returnTo: '/reports/bench',
      returnLabel: 'Bench Report',
      state: { benchState },
    });
  }, [buildBenchState, openIndividual]);

  const summary = report?.summary || { totalOnBench: 0, averageBenchDays: 0, benchByType: { BENCH: 0, CHANGE: 0 }, starCount: 0 };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/reports')}>
            <FaArrowLeft /> Back to Reports
          </BackButton>
          <Title>Bench Report</Title>
        </HeaderLeft>
        <HeaderStats>
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <>
              <HeaderStat>
                <div className="top">
                  <span className="label">On Bench</span>
                  <span className="value">{summary.totalOnBench}</span>
                </div>
              </HeaderStat>
              <HeaderStat>
                <div className="top">
                  <span className="label">Avg. Days</span>
                  <span className="value">{summary.averageBenchDays}</span>
                </div>
              </HeaderStat>
              <HeaderStat>
                <div className="top">
                  <span className="label">Bench</span>
                  <span className="value">{summary.benchByType.BENCH}</span>
                </div>
                <span className="sub">Between assignments</span>
              </HeaderStat>
              <HeaderStat>
                <div className="top">
                  <span className="label">Change</span>
                  <span className="value">{summary.benchByType.CHANGE}</span>
                </div>
                <span className="sub">Requested change</span>
              </HeaderStat>
              <HeaderStat>
                <div className="top">
                  <span className="label">Starred</span>
                  <span className="value">{summary.starCount}</span>
                </div>
                <span className="sub">Top talents</span>
              </HeaderStat>
            </>
          )}
        </HeaderStats>
      </Header>

      <Content>
        <MainSection ref={mainSectionRef}>
          {/* Filters */}
          <FiltersBar>
            <ClusterSelector
              value={clusterId}
              onChange={setClusterId}
              label="Cluster"
              showStructureType // ADR-119
              size="small"
              fullWidth={false}
              sx={{ minWidth: 180 }}
            />

            {businessUnitFacetsFailed ? (
              // Degrade, don't fail (ADR-108 §5): plain hierarchy, scoped by Cluster
              <BusinessUnitSelector
                includeInactive // ADR-120: reports keep ended units, marked "Ended <date>"
                value={businessUnitId}
                onChange={setBusinessUnitId}
                label="Business Unit"
                showStructureType // ADR-119
                size="small"
                fullWidth={false}
                sx={{ minWidth: 180 }}
                clusterId={clusterId || null}
              />
            ) : (
              <BusinessUnitSelector
                includeInactive // ADR-120: reports keep ended units, marked "Ended <date>"
                value={businessUnitId}
                onChange={setBusinessUnitId}
                label="Business Unit"
                showStructureType // ADR-119
                size="small"
                fullWidth={false}
                sx={{ minWidth: 180 }}
                businessUnitsData={businessUnitsData}
                loading={isLoadingBusinessUnits}
                getItemCount={(unit) => getBusinessUnitFacetCount(unit, businessUnitCountMode)}
                itemCountTooltip={BENCH_BUSINESS_UNIT_COUNT_TOOLTIP}
                hideClusterChip={Boolean(clusterId)}
                emptyStateLabel={BENCH_BUSINESS_UNIT_EMPTY_LABEL}
              />
            )}

            {positionFacetsFailed ? (
              // Degrade, don't fail (ADR-110 §6): full position catalog, no counts
              <PositionSelector
                multiple
                value={selectedPositionIds}
                onChange={setSelectedPositionIds}
                label="Position"
                size="small"
                fullWidth={false}
                sx={{ minWidth: 180, maxWidth: 320 }}
              />
            ) : (
              <PositionSelector
                multiple
                value={selectedPositionIds}
                onChange={setSelectedPositionIds}
                label="Position"
                size="small"
                fullWidth={false}
                sx={{ minWidth: 180, maxWidth: 320 }}
                positionsByOrgUnitTypeData={positionsData}
                loading={isLoadingPositions}
                getItemCount={getPositionFacetCount}
                getGroupCount={getPositionFacetCount}
                itemCountTooltip={BENCH_POSITION_COUNT_TOOLTIP}
                emptyStateLabel={BENCH_POSITION_EMPTY_LABEL}
                untypedGroupLabel={BENCH_POSITION_UNTYPED_GROUP_LABEL}
                footerNote={getPositionFacetFooterNote(positionFacet)}
              />
            )}

            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Bench Type</InputLabel>
              <Select
                value={benchType}
                label="Bench Type"
                onChange={(e) => setBenchType(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {Object.values(BENCH_TYPES).map((type) => (
                  <MenuItem key={type} value={type}>
                    {getBenchTypeLabel(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Star</InputLabel>
              <Select
                value={flagAsStar}
                label="Star"
                onChange={(e) => setFlagAsStar(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Starred</MenuItem>
                <MenuItem value="false">Not Starred</MenuItem>
              </Select>
            </FormControl>

            <EmployeeSearchProvider instanceId="bench-report-search">
              <EmployeeSearch
                value={selectedEmployee}
                onChange={setSelectedEmployee}
                label="Individual"
                placeholder="Search individuals..."
                width={260}
                height={40}
                hideLabel
              />
            </EmployeeSearchProvider>

            <TechnologyFilter
              value={selectedTechs}
              onChange={setSelectedTechs}
              searchWidth={350}
              sx={{ flex: 1 }}
            />

          </FiltersBar>

          {/* Sort control */}
          {!isLoading && employees.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <ResultsCount count={summary.totalOnBench} singular="individual" plural="individuals" />
              <SortButton onClick={toggleSortDirection}>
                {orderDirection === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />}
                Bench start date ({orderDirection === 'desc' ? 'newest' : 'oldest'} first)
              </SortButton>
            </div>
          )}

          {/* ADR-117 §2 — the report now degrades rather than 500s when a salary
              cannot be decrypted. Surface that, or the failure is invisible and a
              key-management problem survives unnoticed. */}
          {(report?.warnings || []).map((w) => (
            <Alert key={w.code} severity="warning" sx={{ mb: 2 }}>
              {w.message}
            </Alert>
          ))}

          {/* Employee list */}
          {isLoading ? (
            <LoadingSpinner />
          ) : employees.length === 0 ? (
            <EmptyState
              title="No Bench Individuals Found"
              description="No individuals match the current filters. Try adjusting your search criteria."
            />
          ) : (
            <>
              {employees.map((emp) => (
                <EmployeeRow key={emp.employeeProjectId}>
                  <EmployeeRowMain>
                  <EmployeeAvatar employee={emp.employee} size={40} />

                  <EmployeeInfo>
                    <div
                      className="name name-link"
                      onClick={() => handleNavigateToEmployee(emp)}
                    >
                      {emp.employee.fullName}
                    </div>
                    <div className="detail">
                      {emp.employee.position?.label || 'No position'}
                      {emp.employee.grade?.label && ` • ${emp.employee.grade.label}`}
                    </div>
                    <div className="detail">
                      {[
                        emp.cluster?.name,
                        emp.businessUnit?.name,
                        emp.employee.contractType?.name,
                        emp.employee.workingLocation?.name,
                        emp.benchManager
                          ? `Bench Representative: ${emp.benchManager.fullName}`
                          : emp.directManager ? `Representative: ${emp.directManager.fullName}` : null,
                      ].filter(Boolean).join(' • ')}
                    </div>
                    {emp.technologies && emp.technologies.length > 0 && (() => {
                      const isExpanded = expandedTech[emp.employeeProjectId];
                      const visibleTech = isExpanded ? emp.technologies : emp.technologies.slice(0, 6);
                      const hasMore = emp.technologies.length > 6;
                      return (
                        <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                          {visibleTech.map((tech, i) => (
                            <Tooltip key={i} title={tech.competencyLevel || ''}>
                              <TechBadge $color={tech.competencyColor || '#94A3B8'}>
                                {tech.name}
                              </TechBadge>
                            </Tooltip>
                          ))}
                          {hasMore && !isExpanded && (
                            <TechBadgeMore
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedTech((prev) => ({ ...prev, [emp.employeeProjectId]: true }));
                              }}
                            >
                              +{emp.technologies.length - 6}
                            </TechBadgeMore>
                          )}
                          {hasMore && isExpanded && (
                            <TechBadgeLess
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedTech((prev) => ({ ...prev, [emp.employeeProjectId]: false }));
                              }}
                            >
                              Show less
                            </TechBadgeLess>
                          )}
                        </div>
                      );
                    })()}
                  </EmployeeInfo>

                  <BenchDatesCell
                    status={emp.status}
                    benchDays={emp.benchDays}
                    daysUntilStart={emp.daysUntilStart}
                    benchStartDate={emp.benchStartDate}
                    benchEndDate={emp.benchEndDate}
                    employeeResignDate={emp.employeeResignDate}
                  >
                    <BenchMeta>
                      {emp.benchType !== BENCH_TYPES.BENCH && (
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 600,
                          backgroundColor: getBenchTypeColor(emp.benchType) + '1A',
                          color: getBenchTypeColor(emp.benchType),
                        }}>
                          {getBenchTypeLabel(emp.benchType)}
                        </span>
                      )}
                      {emp.flagAsStar && (
                        <Tooltip title="Top talent">
                          <span style={{ color: '#E65100', fontSize: '14px', display: 'flex' }}>
                            <FaRegStar />
                          </span>
                        </Tooltip>
                      )}
                      {emp.comments && (
                        <CommentPopup html={emp.comments} />
                      )}
                    </BenchMeta>

                    {canReadSalary && emp.salary && (
                      <div style={{ minWidth: '100px', textAlign: 'right', fontSize: '13px', color: '#475569' }}>
                        {formatAmount(emp.salary)} {emp.salaryCurrency}{getSalaryPeriodLabel(emp.salaryPeriod)}
                        {emp.salaryAmountType && (
                          <> · {getSalaryAmountTypeLabel(emp.salaryAmountType)}</>
                        )}
                      </div>
                    )}

                    {/* ADR-117 §1 — a salary that exists but cannot be read must never
                        render as blank: blank reads as "none recorded", which is the
                        opposite conclusion. */}
                    {canReadSalary && emp.salaryStatus === 'UNREADABLE' && (
                      <Tooltip title="A salary is recorded for this individual, but it cannot be decrypted in this environment. It has not been lost — do not re-enter it without checking.">
                        <div style={{ minWidth: '100px', textAlign: 'right', fontSize: '13px', color: '#b45309', cursor: 'help' }}>
                          {emp.salaryCurrency ? `${emp.salaryCurrency} ` : ''}— unreadable
                        </div>
                      </Tooltip>
                    )}
                  </BenchDatesCell>
                  </EmployeeRowMain>

                  <PlacementActivitySummary
                    activeFtProject={emp.activeFtProject}
                    placementActivitySummary={emp.placementActivitySummary}
                    clusterName={emp.cluster?.name}
                    onOpenTab={() => handleOpenPlacementActivity(emp)}
                  />
                </EmployeeRow>
              ))}

              {report?.pageInfo?.hasMore && (
                <LoadMoreButton onClick={handleLoadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? (
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                  ) : null}
                  Load More
                </LoadMoreButton>
              )}
            </>
          )}
        </MainSection>
      </Content>
    </Container>
  );
};

export default BenchReport;
